import { reactive, readonly } from 'vue';
import axios from 'axios';
import { OFFLINE_MODE_ENABLED } from '@/services/offline/offlineFeature';

export const CONNECTIVITY_MODES = {
  ONLINE: 'online',
  BROWSER_OFFLINE: 'browser_offline',
  BACKEND_UNAVAILABLE: 'backend_unavailable',
  CHECKING: 'checking',
};

const DEFAULT_TIMEOUT_MS = 3000;
const MONITOR_INTERVAL_MS = 15000;

const state = reactive({
  browserOnline: OFFLINE_MODE_ENABLED ? getBrowserOnlineStatus() : true,
  backendAvailable: OFFLINE_MODE_ENABLED ? null : true,
  backendLastCheckedAt: null,
  backendLastSuccessAt: null,
  backendLastFailureAt: null,
  backendFailureReason: null,
  mode: OFFLINE_MODE_ENABLED ? CONNECTIVITY_MODES.CHECKING : CONNECTIVITY_MODES.ONLINE,
  manualOffline: false,
});

const listeners = new Set();
let monitoringStarted = false;
let monitoringTimer = null;
let consecutiveBackendUnavailableChecks = 0;
let backendCheckPromise = null;

function nowIso() {
  return new Date().toISOString();
}

function getApiBaseUrl() {
  return process.env.VUE_APP_API_URL || '';
}

function notifyListeners() {
  const snapshot = getConnectivityState();
  listeners.forEach((callback) => {
    try {
      callback(snapshot);
    } catch (error) {
      console.warn('Connectivity subscriber failed:', error);
    }
  });
}

function setMode(mode) {
  state.mode = mode;
  notifyListeners();
}

function refreshMode() {
  if (!OFFLINE_MODE_ENABLED) {
    setMode(CONNECTIVITY_MODES.ONLINE);
    return;
  }
  if (!state.browserOnline) {
    setMode(CONNECTIVITY_MODES.BROWSER_OFFLINE);
    return;
  }

  if (state.manualOffline) {
    setMode(CONNECTIVITY_MODES.BACKEND_UNAVAILABLE);
    return;
  }

  if (state.backendAvailable === false) {
    setMode(CONNECTIVITY_MODES.BACKEND_UNAVAILABLE);
    return;
  }

  if (state.backendAvailable === null) {
    setMode(CONNECTIVITY_MODES.CHECKING);
    return;
  }

  setMode(CONNECTIVITY_MODES.ONLINE);
}

function backendCheckUrl(apiBaseUrl = getApiBaseUrl()) {
  return `${apiBaseUrl}/v1/api/partner/health`;
}

function backendCheckHeaders() {
  return {};
}

export function getBrowserOnlineStatus() {
  return typeof navigator !== 'undefined' ? navigator.onLine : true;
}

export function isBackendUnavailableError(error) {
  const status = error?.response?.status || error?.status || 0;
  const code = String(error?.code || '').toUpperCase();
  const message = String(error?.message || '').toLowerCase();

  if ([502, 503, 504].includes(Number(status))) return true;
  if (Number(status) === 0) return true;
  if (code === 'ECONNABORTED' || code === 'ERR_NETWORK' || code === 'ETIMEDOUT') return true;
  if (message.includes('timeout')) return true;
  if (message.includes('network error')) return true;
  if (message.includes('failed to fetch')) return true;
  if (message.includes('connection refused')) return true;
  if (message.includes('dns')) return true;

  if ([400, 401, 403, 404, 409, 422].includes(Number(status))) return false;

  return false;
}

export function markBackendUnavailable(reason = 'Backend no disponible.') {
  if (!OFFLINE_MODE_ENABLED) return;
  state.backendAvailable = false;
  state.backendLastCheckedAt = nowIso();
  state.backendLastFailureAt = state.backendLastCheckedAt;
  state.backendFailureReason = reason;
  refreshMode();
}

export function markBackendAvailable() {
  state.backendAvailable = true;
  state.backendLastCheckedAt = nowIso();
  state.backendLastSuccessAt = state.backendLastCheckedAt;
  state.backendFailureReason = null;
  consecutiveBackendUnavailableChecks = 0;
  state.manualOffline = false;
  refreshMode();
}

export function enableManualOfflineMode(reason = 'Modo offline manual activado.') {
  if (!OFFLINE_MODE_ENABLED) return;
  state.manualOffline = true;
  state.backendFailureReason = reason;
  refreshMode();
}

export async function retryConnectivity() {
  state.manualOffline = false;
  return checkBackendAvailability({ force: true });
}

export async function checkBackendAvailability(options = {}) {
  if (!OFFLINE_MODE_ENABLED) return getConnectivityState();
  if (backendCheckPromise) return backendCheckPromise;

  backendCheckPromise = performBackendAvailabilityCheck(options);
  try {
    return await backendCheckPromise;
  } finally {
    backendCheckPromise = null;
  }
}

async function performBackendAvailabilityCheck(options = {}) {
  state.browserOnline = getBrowserOnlineStatus();
  if (!state.browserOnline) {
    state.backendAvailable = null;
    state.backendLastCheckedAt = nowIso();
    refreshMode();
    return getConnectivityState();
  }

  const {
    apiBaseUrl = getApiBaseUrl(),
    timeoutMs = DEFAULT_TIMEOUT_MS,
    showChecking = false,
  } = options;

  state.backendLastCheckedAt = nowIso();
  if (showChecking) {
    setMode(CONNECTIVITY_MODES.CHECKING);
  }

  try {
    await axios.get(backendCheckUrl(apiBaseUrl), {
      headers: backendCheckHeaders(),
      timeout: timeoutMs,
      params: { _health_ts: Date.now() },
    });
    markBackendAvailable();
  } catch (error) {
    if (isBackendUnavailableError(error)) {
      consecutiveBackendUnavailableChecks += 1;
      if (consecutiveBackendUnavailableChecks >= 2) {
        markBackendUnavailable(error?.message || 'No se pudo conectar con el backend.');
      } else {
        state.backendLastCheckedAt = nowIso();
        state.backendLastFailureAt = state.backendLastCheckedAt;
        state.backendFailureReason = error?.message || 'Comprobacion de backend fallida.';
        refreshMode();
      }
    } else if (Number(error?.response?.status) >= 500) {
      state.backendLastCheckedAt = nowIso();
      state.backendLastFailureAt = state.backendLastCheckedAt;
      state.backendFailureReason = error?.message || 'El servidor devolvio un error temporal.';
      refreshMode();
    } else {
      markBackendAvailable();
    }
  }

  return getConnectivityState();
}

export function getConnectivityState() {
  return {
    browserOnline: state.browserOnline,
    backendAvailable: state.backendAvailable,
    backendLastCheckedAt: state.backendLastCheckedAt,
    backendLastSuccessAt: state.backendLastSuccessAt,
    backendLastFailureAt: state.backendLastFailureAt,
    backendFailureReason: state.backendFailureReason,
    mode: state.mode,
    manualOffline: state.manualOffline,
  };
}

export function subscribeToConnectivityChanges(callback) {
  listeners.add(callback);
  callback(getConnectivityState());
  return () => listeners.delete(callback);
}

export function shouldUseOfflineMode() {
  if (!OFFLINE_MODE_ENABLED) return false;
  return state.manualOffline ||
    state.mode === CONNECTIVITY_MODES.BROWSER_OFFLINE ||
    state.mode === CONNECTIVITY_MODES.BACKEND_UNAVAILABLE;
}

function handleBrowserOnline() {
  state.browserOnline = true;
  refreshMode();
  void checkBackendAvailability({ force: true });
}

function handleBrowserOffline() {
  state.browserOnline = false;
  state.backendAvailable = null;
  refreshMode();
}

export function startConnectivityMonitoring() {
  if (!OFFLINE_MODE_ENABLED) return;
  if (typeof window === 'undefined' || monitoringStarted) return;
  monitoringStarted = true;
  window.addEventListener('online', handleBrowserOnline);
  window.addEventListener('offline', handleBrowserOffline);
  monitoringTimer = window.setInterval(() => {
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
      return;
    }
    void checkBackendAvailability().catch(() => {});
  }, MONITOR_INTERVAL_MS);
  void checkBackendAvailability({ force: true });
}

export function stopConnectivityMonitoring() {
  if (typeof window === 'undefined') return;
  monitoringStarted = false;
  if (monitoringTimer) {
    window.clearInterval(monitoringTimer);
    monitoringTimer = null;
  }
  window.removeEventListener('online', handleBrowserOnline);
  window.removeEventListener('offline', handleBrowserOffline);
}

export const connectivityStatus = readonly(state);
